import { useState, useEffect } from 'react';
import { useIsSuperAdmin } from '../stores/authStore';
import forgeService from '../services/forgeService';

interface UseOrgForgeReturn {
  /** Whether the user's organisation has at least one domain linked to a Forge. */
  hasForge: boolean;
  isLoading: boolean;
}

/**
 * Returns whether the current user's organisation has a Forge associated
 * (i.e. at least one domain with a forge_id).
 *
 * Super-admins always receive hasForge=true immediately (no fetch needed).
 * The check is done once per mount via a lightweight DB-only backend endpoint.
 */
export function useOrgForge(): UseOrgForgeReturn {
  const isSuperAdmin = useIsSuperAdmin();
  const [hasForge, setHasForge] = useState<boolean>(isSuperAdmin);
  const [isLoading, setIsLoading] = useState<boolean>(!isSuperAdmin);

  useEffect(() => {
    if (isSuperAdmin) {
      setHasForge(true);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    forgeService
      .checkOrgHasForge()
      .then((result) => {
        if (!cancelled) setHasForge(result);
      })
      .catch(() => {
        if (!cancelled) setHasForge(false);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin]);

  return { hasForge, isLoading };
}
